import { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import UploadDocumentModal from '@/pages/documents/components/UploadDocumentModal';
import ReviewDocumentModal from '@/pages/documents/components/ReviewDocumentModal';
import DocumentDetailModal from '@/pages/documents/components/DocumentDetailModal';
import DocumentThumbnail from '@/components/feature/DocumentThumbnail';
import KycStatusTimeline from './components/KycStatusTimeline';

interface KycDoc {
  id: number;
  doc_type: string;
  document_type_name: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  verification_status: string;
  review_reason: string | null;
  reviewed_at: string | null;
  reviewer_name: string | null;
  uploaded_by_name: string | null;
}

interface AuditEntry {
  action: string;
  userName: string;
  timestamp: string;
}

interface KycDetail {
  id: number;
  requestNumber: string;
  customerId: number;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    address: string;
  };
  status: string;
  riskScore: number;
  notes: string;
  rejectionReason: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
  documents: KycDoc[];
  auditTrail: AuditEntry[];
}

export default function KycDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: kyc, loading, refetch } = useApiQuery<KycDetail>({ endpoint: `/kyc/${id}` });
  const { put } = useApi();
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'company_admin' || user?.role === 'kyc_officer';

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [reviewDoc, setReviewDoc] = useState<{ id: number; file_name: string; mime_type?: string } | null>(null);
  const [detailDoc, setDetailDoc] = useState<number | null>(null);
  const [kycActionLoading, setKycActionLoading] = useState(false);
  const [kycActionError, setKycActionError] = useState<string | null>(null);

  const [notes, setNotes] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [notesSaved, setNotesSaved] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [riskScore, setRiskScore] = useState<number | ''>('');
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState<string | null>(null);
  const [riskSaved, setRiskSaved] = useState(false);

  // Sync local state when kyc data loads
  useEffect(() => {
    if (kyc) {
      setRiskScore(kyc.riskScore);
      setNotes(kyc.notes || '');
      setNotesSaved(false);
      setRiskSaved(false);
    }
  }, [kyc?.id]);

  const statusColors: Record<string, string> = {
    draft: 'bg-stone-100 text-stone-600',
    submitted: 'bg-sky-50 text-sky-700',
    'under review': 'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  };

  const handleKycApprove = async () => {
    if (!id) return;
    setKycActionLoading(true);
    setKycActionError(null);
    try {
      await put(`/kyc/${id}/review`, { decision: 'approve' });
      refetch();
    } catch (err) {
      setKycActionError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setKycActionLoading(false);
    }
  };

  const handleKycReject = async () => {
    if (!id) return;
    const reason = prompt('Enter rejection reason:');
    if (!reason || !reason.trim()) return;
    setKycActionLoading(true);
    setKycActionError(null);
    try {
      await put(`/kyc/${id}/review`, { decision: 'reject', reason: reason.trim() });
      refetch();
    } catch (err) {
      setKycActionError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setKycActionLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!id) return;
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      await put(`/kyc/${id}/submit`, {});
      refetch();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit for review');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSaveNotes = useCallback(async () => {
    if (!id) return;
    setNotesError(null);
    setNotesSaved(false);
    setNotesLoading(true);
    try {
      await put(`/kyc/${id}`, { notes: notes.trim() });
      setNotesSaved(true);
      refetch();
    } catch (err) {
      setNotesError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setNotesLoading(false);
    }
  }, [id, notes, put, refetch]);

  const handleSaveRiskScore = useCallback(async () => {
    if (!id || riskScore === '') return;
    const score = Number(riskScore);
    if (score < 0 || score > 100) {
      setRiskError('Risk score must be between 0 and 100');
      return;
    }
    setRiskError(null);
    setRiskSaved(false);
    setRiskLoading(true);
    try {
      await put(`/kyc/${id}`, { riskScore: score, riskLevel: score > 70 ? 'high' : score > 40 ? 'medium' : 'low' });
      setRiskSaved(true);
      refetch();
    } catch (err) {
      setRiskError(err instanceof Error ? err.message : 'Failed to save risk score');
    } finally {
      setRiskLoading(false);
    }
  }, [id, riskScore, put, refetch]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin" />
          <span className="text-stone-500 text-sm">Loading KYC details...</span>
        </div>
      </div>
    );
  }

  if (!kyc) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-list-3-line text-stone-300 text-3xl mb-2" />
          <p className="text-stone-500 text-sm">KYC request not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/kyc-requests"
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
          >
            <i className="ri-arrow-left-line text-lg" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-stone-900">{kyc.requestNumber}</h1>
            <p className="text-stone-500 text-sm">KYC Request Details</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${statusColors[kyc.status] || 'bg-stone-100 text-stone-700'}`}>
            {kyc.status}
          </span>
          {kyc.status === 'draft' && isAdmin && (
            <button
              onClick={handleSubmitForReview}
              disabled={submitLoading}
              className="bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              {submitLoading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
              Submit for Review
            </button>
          )}
          {(kyc.status === 'submitted' || kyc.status === 'under review') && isAdmin && (
            <>
              <button
                onClick={handleKycApprove}
                disabled={kycActionLoading}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-check-line" />
                Approve
              </button>
              <button
                onClick={handleKycReject}
                disabled={kycActionLoading}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-close-line" />
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {kycActionError && (
        <div className="mb-4 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {kycActionError}
        </div>
      )}

      {submitError && (
        <div className="mb-4 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {submitError}
        </div>
      )}

      <KycStatusTimeline
        status={kyc.status}
        submittedAt={kyc.submittedAt}
        reviewedAt={kyc.reviewedAt}
        createdAt={kyc.createdAt ?? new Date().toISOString()}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-stone-900">Customer Information</h3>
              <Link
                to={`/customers/${kyc.customerId}`}
                className="text-brand-600 hover:text-brand-700 text-sm font-medium flex items-center gap-1 transition-colors"
              >
                View Profile
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Full Name</p>
                <p className="text-stone-900 text-sm font-medium">{kyc.customer.firstName} {kyc.customer.lastName}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Email</p>
                <p className="text-stone-900 text-sm">{kyc.customer.email}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Phone</p>
                <p className="text-stone-900 text-sm">{kyc.customer.phone}</p>
              </div>
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Date of Birth</p>
                <p className="text-stone-900 text-sm">{kyc.customer.dateOfBirth ? new Date(kyc.customer.dateOfBirth).toLocaleDateString() : '-'}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Address</p>
                <p className="text-stone-900 text-sm">{kyc.customer.address}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-stone-900">Submitted Documents</h3>
              <button
                onClick={() => setUploadModalOpen(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-add-line" />
                Upload Document
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {kyc.documents.map((doc) => (
                <div key={doc.id} className="flex items-start gap-3 p-3 rounded-lg border border-stone-100 hover:border-brand-200 transition-colors cursor-pointer" onClick={() => setDetailDoc(doc.id)}>
                  <DocumentThumbnail
                    documentId={doc.id}
                    mimeType={doc.mime_type}
                    fileName={doc.file_name}
                    size="md"
                    onClick={() => setDetailDoc(doc.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-900 text-sm font-medium truncate">{doc.file_name}</p>
                    <p className="text-stone-400 text-xs">{doc.document_type_name || doc.doc_type}</p>
                    {doc.uploaded_by_name && (
                      <p className="text-stone-400 text-[10px]">Uploaded by {doc.uploaded_by_name}</p>
                    )}
                    {doc.verification_status === 'failed' && doc.review_reason && (
                      <div className="mt-1 bg-red-50 border border-red-100 rounded-md px-2 py-1">
                        <p className="text-red-700 text-[10px]"><strong>Rejected:</strong> {doc.review_reason}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      doc.verification_status === 'verified' ? 'bg-emerald-50 text-emerald-700' :
                      doc.verification_status === 'failed' ? 'bg-red-50 text-red-700' :
                      'bg-amber-50 text-amber-700'
                    }`}>
                      {doc.verification_status}
                    </span>
                    {isAdmin && doc.verification_status !== 'verified' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setReviewDoc({ id: doc.id, file_name: doc.file_name, mime_type: doc.mime_type }); }}
                        className="text-brand-600 hover:text-brand-700 text-[10px] font-medium cursor-pointer"
                      >
                        Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {kyc.documents.length === 0 && (
                <div className="col-span-full text-center py-6">
                  <i className="ri-file-list-3-line text-stone-300 text-2xl mb-2" />
                  <p className="text-stone-400 text-sm">No documents attached yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Risk Score */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-4">Risk Assessment</h3>
            {riskError && (
              <div className="mb-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2">
                <i className="ri-error-warning-line" />
                {riskError}
              </div>
            )}
            {riskSaved && (
              <div className="mb-3 bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2">
                <i className="ri-check-line" />
                Risk score saved.
              </div>
            )}
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center">
                <span className="text-xl font-bold text-stone-900">{kyc.riskScore}</span>
              </div>
              <div className="flex-1">
                <div className="h-3 bg-stone-100 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full ${kyc.riskScore > 70 ? 'bg-red-500' : kyc.riskScore > 40 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${kyc.riskScore}%` }}
                  />
                </div>
                <p className="text-stone-500 text-xs">
                  Risk Level: <span className="font-medium text-stone-900">{kyc.riskScore > 70 ? 'High' : kyc.riskScore > 40 ? 'Medium' : 'Low'}</span>
                </p>
              </div>
            </div>
            {isAdmin && kyc.status !== 'approved' && kyc.status !== 'rejected' && (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-stone-400 uppercase tracking-wider mb-1">Update Risk Score (0-100)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={riskScore}
                    onChange={(e) => { setRiskScore(e.target.value === '' ? '' : Number(e.target.value)); setRiskSaved(false); }}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <button
                  onClick={handleSaveRiskScore}
                  disabled={riskLoading || riskScore === ''}
                  className="mt-5 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold px-4 py-2 rounded-lg text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                >
                  {riskLoading ? <i className="ri-loader-4-line animate-spin" /> : 'Save Score'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-stone-900">Audit Trail</h3>
            </div>
            <div className="flex flex-col gap-4">
              {kyc.auditTrail.map((entry, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      entry.action.includes('reject') || entry.action.includes('delete')
                        ? 'bg-red-400'
                        : entry.action.includes('approve') || entry.action.includes('create')
                        ? 'bg-emerald-400'
                        : entry.action.includes('submit') || entry.action.includes('review')
                        ? 'bg-brand-400'
                        : 'bg-stone-300'
                    }`} />
                    {index < kyc.auditTrail.length - 1 && (
                      <div className="w-px flex-1 bg-stone-200 my-1" />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-stone-900 text-sm font-medium capitalize">{entry.action.replace(/_/g, ' ')}</p>
                    <p className="text-stone-500 text-xs">by {entry.userName}</p>
                    <p className="text-stone-400 text-xs">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-stone-100 p-6">
            <h3 className="font-bold text-stone-900 mb-3">Internal Notes</h3>
            {notesError && (
              <div className="mb-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2">
                <i className="ri-error-warning-line" />
                {notesError}
              </div>
            )}
            {notesSaved && (
              <div className="mb-3 bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2">
                <i className="ri-check-line" />
                Notes saved.
              </div>
            )}
            <textarea
              rows={4}
              value={notes}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none"
              placeholder="Add notes about this KYC request..."
              onChange={(e) => { setNotes(e.target.value); setNotesSaved(false); }}
            />
            <button
              onClick={handleSaveNotes}
              disabled={notesLoading}
              className="mt-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold px-4 py-2 rounded-lg text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              {notesLoading ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>

      <UploadDocumentModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={refetch}
        kycRequestId={Number(id)}
      />

      <ReviewDocumentModal
        isOpen={!!reviewDoc}
        onClose={() => setReviewDoc(null)}
        onSuccess={refetch}
        documentId={reviewDoc?.id ?? null}
        documentName={reviewDoc?.file_name ?? ''}
        mimeType={reviewDoc?.mime_type ?? ''}
      />

      <DocumentDetailModal
        isOpen={!!detailDoc}
        onClose={() => setDetailDoc(null)}
        documentId={detailDoc ?? null}
        onReviewSuccess={refetch}
      />
    </div>
  );
}